import React, { useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { usePatientPipelineStatus } from '../patient/usePatientPipelineStatus';

/**
 * Task #28 (register theme R10): tell the clinician when this patient's Digital
 * Twin has not finished building yet, because until it has, the assistant is
 * reasoning without the trajectory narrative and the organ scores that normally
 * inform its recommendations.
 *
 * WHY IT LIVES HERE AND NOT IN THE RESUME DIALOG
 * ----------------------------------------------
 * The obvious home looked like the "Resume patient session?" dialog, and that
 * dialog is indeed shown when the clinician comes back from the Admin page. But
 * it is shown ONLY for a stored session (`source` of 'tab' or 'recovered'). A
 * FRESH SMART launch skips it entirely and goes straight to the import flow.
 *
 * That is exactly backwards for this warning: a fresh launch is the moment the
 * twin is LEAST likely to be built, because the build job has only just been
 * triggered. Putting the notice solely in the resume dialog would have hidden it
 * precisely when it matters most.
 *
 * So it is mounted where every entry path converges instead — the gate's `ready`
 * branch, immediately before the chat is handed the screen. Fresh launch, return
 * from Admin, and cross-tab recovery all pass through there, which satisfies
 * "anytime we enter the chat area" with ONE mount point rather than three.
 *
 * DESIGN CHOICES WORTH KEEPING
 * ----------------------------
 * - **Informative, not blocking.** The clinician can still work; the twin
 *   affects how well-grounded the answers are, not whether they function.
 * - **Silent until we actually know.** Nothing renders while the status is still
 *   loading, and nothing renders if the status call FAILED. A failed lookup is
 *   not evidence that the twin is missing, and claiming otherwise would be the
 *   same false-absence mistake the backend keeps having to fix.
 * - **Never shown when ready**, so a fully-prepared patient adds no friction.
 */

export interface TwinReadinessNoticeProps {
  /** Backend patient id. Empty disables the check entirely. */
  patientId: string;
}

/** True only when we have a definite answer AND that answer is "not ready". */
export function shouldWarnAboutTwin(
  status: { ready?: boolean; twin?: { ready: boolean }; summary?: { ready: boolean } } | null,
  error: string | null,
): boolean {
  // An error means we do not know. Do not guess in either direction.
  if (error) return false;
  if (!status) return false;
  if (status.ready) return false;
  return true;
}

/** What is still being prepared, in the clinician's terms. */
export function readinessDetail(status: {
  stage?: string;
  twin?: { ready: boolean };
  summary?: { ready: boolean };
} | null): string {
  if (!status) return '';
  const pending: string[] = [];
  if (status.twin && !status.twin.ready) pending.push('the digital twin');
  if (status.summary && !status.summary.ready) pending.push('the trajectory summary');
  if (pending.length === 0) {
    return status.stage === 'scoring' ? 'Organ scores are still being calculated.' : '';
  }
  const joined =
    pending.length === 1 ? pending[0] : `${pending.slice(0, -1).join(', ')} and ${pending[pending.length - 1]}`;
  return `Still building: ${joined}.`;
}

export function TwinReadinessNotice({ patientId }: TwinReadinessNoticeProps): React.ReactElement | null {
  const { status, error } = usePatientPipelineStatus(patientId);
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;
  if (!shouldWarnAboutTwin(status, error)) return null;

  const detail = readinessDetail(status);

  return (
    <div
      role="status"
      aria-live="polite"
      className="mx-auto mb-2 flex w-full max-w-3xl items-start gap-2.5 rounded-xl border border-(--status-attention)/40 bg-status-attention-bg px-3.5 py-2.5 text-left"
    >
      <AlertTriangle className="mt-0.5 size-4 shrink-0 text-status-attention-text" aria-hidden />
      <div className="min-w-0 flex-1 text-xs leading-relaxed text-status-attention-text">
        <span className="font-medium">This patient&apos;s background analysis is still running.</span>{' '}
        Recommendations in this session may be less accurate than usual, because they are being made
        without the full patient picture. {detail} You can keep working — or come back once it has
        finished for a more complete answer.
      </div>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss background analysis notice"
        className="shrink-0 cursor-pointer rounded-md p-1 text-status-attention-text/70 transition-colors hover:bg-status-attention-bg hover:text-status-attention-text"
      >
        <X className="size-3.5" aria-hidden />
      </button>
    </div>
  );
}
